import { WarningCircleIcon } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/lib/utils';
import { formatDate } from '~/lib/local-datetime';
import { useAppStorage, type AppStorage } from '~/hooks/queries/use-app-storage';

/**
 * FRONT-3007. How full an app's data storage is, for its owner: a bar in the
 * Edit page's Data store card and on the Analytics screen, and a badge on the
 * Apps list row when it is close to full. Callers mount these for the owner only; the server
 * refuses everyone else anyway.
 */

export const AMBER_PERCENT = 70;
export const RED_PERCENT = 90;

export type StorageTone = 'ok' | 'amber' | 'red';

export function storageTone(s: Pick<AppStorage, 'percent' | 'full'>): StorageTone {
  if (s.full || s.percent >= RED_PERCENT) return 'red';
  if (s.percent >= AMBER_PERCENT) return 'amber';
  return 'ok';
}

const BAR_CLASS: Record<StorageTone, string> = {
  ok: 'bg-success',
  amber: 'bg-amber-500',
  red: 'bg-destructive',
};

const BADGE_CLASS: Record<Exclude<StorageTone, 'ok'>, string> = {
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  red: 'border-destructive/30 bg-destructive/10 text-destructive',
};

const count = new Intl.NumberFormat('en-US');

function megabytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb === 0) return '0';
  return mb < 10 ? String(Math.max(0.1, Math.round(mb * 10) / 10)) : String(Math.round(mb));
}

/** "3 Nov" in the viewer's own zone and conventions, with the year when it is not this year's. */
export function formatFullDay(iso: string, now: Date = new Date(), timeZone?: string): string {
  const zone = timeZone ? { timeZone } : {};
  const sameYear = formatDate(iso, { ...zone, year: 'numeric' }) === formatDate(now, { ...zone, year: 'numeric' });
  return formatDate(iso, { ...zone, day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** Mirrors the server's PROJECTED_WITHIN_DAYS: the date shows when the list badge and bell notice would. */
export const SHOW_DATE_WITHIN_DAYS = 14;

/** "24% used" — floored so a nearly full app never reads 100% before it is. */
export function storagePercentLabel(s: Pick<AppStorage, 'percent' | 'full' | 'records' | 'bytes'>): string {
  // Full can arrive below 100%: the server calls it once the next average-sized entry no longer fits.
  if (s.full) return 'Full';
  const pct = Math.min(99, Math.floor(Math.max(0, s.percent)));
  const hasStoredData = s.records > 0 || s.bytes > 0;
  if (pct === 0 && hasStoredData) return 'Under 1% used';
  return `${pct}% used`;
}

/** "7,000 / 50,000 entries · 12 / 50 MB" */
export function storageLimits(s: AppStorage): string {
  return `${count.format(s.records)} / ${count.format(s.maxRecords)} entries · ${megabytes(s.bytes)} / ${megabytes(s.maxBytes)} MB`;
}

/** The same advice the bell notice gives at every level. */
const CONTACT = 'Contact your customer success team to plan more space or an archive.';

/** The one line that needs the owner's attention, or null while there is nothing to act on. */
export function storageAlert(
  s: AppStorage,
  now: Date = new Date(),
  timeZone?: string,
): { tone: 'amber' | 'red'; text: string } | null {
  if (s.full) return { tone: 'red', text: `Full, so new entries are refused. ${CONTACT}` };
  const tone = storageTone(s) === 'red' ? 'red' : 'amber';
  if (s.expectedFullAt && s.daysToFull != null && s.daysToFull <= SHOW_DATE_WITHIN_DAYS) {
    return { tone, text: `Fills around ${formatFullDay(s.expectedFullAt, now, timeZone)} at the current pace. ${CONTACT}` };
  }
  if (tone === 'red') return { tone, text: `Nearly full. ${CONTACT}` };
  return null;
}

/**
 * `inUse` false (the Data store switched off) hides the bar unless the app
 * still holds entries — a backend handler or chat can write them regardless.
 */
export function AppStorageBar({ slug, className, inUse = true }: { slug: string; className?: string; inUse?: boolean }) {
  const { data, isPending, isError } = useAppStorage(slug);
  if (!inUse && !(data && (data.records > 0 || data.bytes > 0))) return null;
  return (
    <section aria-label="Data storage" data-testid="app-storage" className={cn('flex flex-col gap-1.5', className)}>
      {isPending || isError ? <h3 className="text-xs font-medium text-foreground">Storage</h3> : null}
      {isPending ? (
        <Skeleton className="h-8 w-full" />
      ) : isError ? (
        <p data-testid="app-storage-error" className="text-xs text-muted-foreground">
          The storage figures couldn't be loaded right now.
        </p>
      ) : (
        <StorageBarBody storage={data} />
      )}
    </section>
  );
}

const ALERT_CLASS: Record<'amber' | 'red', string> = {
  amber: 'text-amber-600 dark:text-amber-300',
  red: 'text-destructive',
};

function StorageBarBody({ storage }: { storage: AppStorage }) {
  const tone = storageTone(storage);
  const filled = storage.full ? 100 : Math.min(100, Math.max(0, storage.percent));
  const label = storagePercentLabel(storage);
  const alert = storageAlert(storage);
  return (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium text-foreground">Storage</h3>
        <span data-testid="app-storage-percent" className="text-xs text-muted-foreground tabular-nums">
          {label}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Data storage used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.floor(filled)}
        aria-valuetext={label}
        data-tone={tone}
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      >
        <div className={cn('h-full rounded-full', BAR_CLASS[tone])} style={{ width: `${filled}%` }} />
      </div>
      <p data-testid="app-storage-limits" className="text-xs text-muted-foreground tabular-nums">
        {storageLimits(storage)}
      </p>
      {alert && (
        <p data-testid="app-storage-alert" data-tone={alert.tone} className={cn('flex items-start gap-1.5 text-xs', ALERT_CLASS[alert.tone])}>
          <WarningCircleIcon size={12} weight="fill" className="mt-0.5 shrink-0" aria-hidden="true" />
          {alert.text}
        </p>
      )}
    </>
  );
}

/** The Apps list badge: shown when the app fills within 14 days, is at 90%, or is full. */
export function AppStorageBadge({ storage }: { storage: AppStorage | null | undefined }) {
  if (!storage || storage.level < 1) return null;
  const tone = storageTone(storage) === 'red' || storage.level >= 2 ? 'red' : 'amber';
  let label = 'Storage filling';
  if (storage.full) {
    label = 'Storage full';
  } else if (storage.level >= 2) {
    label = `Storage ${Math.min(99, Math.floor(storage.percent))}%`;
  } else if (storage.expectedFullAt) {
    label = `Storage fills ~${formatFullDay(storage.expectedFullAt)}`;
  }
  return (
    <Badge
      variant="outline"
      data-testid="app-storage-badge"
      className={cn('text-[length:var(--text-2xs)] uppercase tracking-wide inline-flex items-center gap-1', BADGE_CLASS[tone])}
    >
      <WarningCircleIcon size={10} weight="fill" />
      {label}
    </Badge>
  );
}
